import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { ArrowLeft, Buildings, MapPin, PencilSimple, Tag as TagIcon } from "@phosphor-icons/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Page } from "../../components/Page";
import { WithAuthorization } from "../../context";
import { routes } from "../../config/paths";
import { useGetSuppliersYearlyData } from "../../hooks/query";
import { constructQueryParamsString } from "../../utils";
import { REPORTING_YEARS } from "./suppliersMock";
import SupplierStatusBadge from "./components/SupplierStatusBadge";
import SupplierFormModal from "./components/SupplierFormModal";
import SupplierDashboard from "./components/detail/SupplierDashboard";
import SupplierTasks from "./components/detail/SupplierTasks";
import SupplierUsers from "./components/detail/SupplierUsers";
import SupplierSpend from "./components/detail/SupplierSpend";

// Dedicated page for a single supplier: header + editable details, KPI/graph
// dashboard, data tasks, users, and spend. Supplier data passed via router state.
function SupplierDetail() {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const { state: routeState } = useLocation();
  const editModal = useDisclosure();

  const [supplier, setSupplier] = useState(routeState?.supplier ?? null);
  const [divisionFilter] = useState([]);

  // The router-state supplierId is a snapshot from the list page's own async
  // UUID lookup — it can be null if that lookup hadn't resolved yet at click
  // time. Re-resolve directly by name here so this page never depends on the
  // list page's timing.
  const uuidQueryString = useMemo(
    () => (supplier?.name ? constructQueryParamsString({ supplier: supplier.name, years: REPORTING_YEARS, showSupplierWithoutData: true }) : null),
    [supplier?.name]
  );
  const { data: resolvedYearlyData } = useGetSuppliersYearlyData(uuidQueryString ?? "", {
    enabled: !supplier?.supplierId && !!supplier?.name,
    cacheTime: 60,
  });

  useEffect(() => {
    if (!supplier?.supplierId && resolvedYearlyData?.[0]?.supplierId) {
      setSupplier((prev) => (prev ? { ...prev, supplierId: resolvedYearlyData[0].supplierId } : prev));
    }
  }, [resolvedYearlyData, supplier?.supplierId]);

  const goBack = () => navigate(routes.supplyChainSupplierData);

  if (!supplier) {
    return (
      <Page>
        <Box p={4} mx={{ base: 4, md: "10%", xl: "15%" }}>
          <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={goBack} mb={4}>
            Suppliers
          </Button>
          <Text color="gray.500">Supplier not found. Please navigate here from the suppliers list.</Text>
        </Box>
      </Page>
    );
  }

  const location = { address: supplier.address, state: supplier.state, country: supplier.country };
  const handleSave = (updated) => setSupplier((prev) => ({ ...prev, ...updated }));

  return (
    <Page>
      <Box p={4} mx={{ base: 4, md: "10%", xl: "15%" }} bg="pureWhite">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={goBack} mb={4} pl={0}>
          <Text fontSize="body.l" fontWeight="600" color="gray.700">Suppliers</Text>
        </Button>

        {/* Header + details */}
        <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap" mb={8}>
          <Box>
            <HStack spacing={3} mb={2}>
              <Text fontSize="heading.3xl" fontWeight="700" color="darkTeal">{supplier.name}</Text>
              <SupplierStatusBadge status={supplier.status} />
            </HStack>
            <VStack align="start" spacing={1.5}>
              <HStack spacing={5} flexWrap="wrap">
                <HStack spacing={2}>
                  <TagIcon size={15} color="#A0AEC0" />
                  <Text fontSize="body.m" color="gray.600">{supplier.category}</Text>
                </HStack>
                <HStack spacing={2}>
                  <Buildings size={15} color="#A0AEC0" />
                  <Text fontSize="body.m" color="gray.600">No divisions</Text>
                </HStack>
              </HStack>
              <HStack spacing={2} align="flex-start">
                <Box mt={0.5}><MapPin size={15} color="#A0AEC0" /></Box>
                <Text fontSize="body.m" color="gray.600">
                  {[location.address, location.state, location.country].filter((p) => p && p !== "—").join(", ") || "No address on file"}
                </Text>
              </HStack>
            </VStack>
          </Box>
          <HStack spacing={3} align="flex-start">
            <Button
              variant="secondary"
              borderRadius="full"
              size="sm"
              leftIcon={<PencilSimple size={14} />}
              onClick={editModal.onOpen}
            >
              Edit details
            </Button>
          </HStack>
        </Flex>

        <VStack align="stretch" spacing={10}>
          <SupplierDashboard supplier={supplier} />
          <SupplierTasks supplier={supplier} divisionFilter={divisionFilter} />
          <SupplierUsers supplier={supplier} />
          <SupplierSpend supplier={supplier} />
        </VStack>
      </Box>

      <SupplierFormModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        onSave={handleSave}
        supplier={supplier}
      />
    </Page>
  );
}

export default WithAuthorization(SupplierDetail);
